import { gql, useMutation, useQuery, useSubscription } from '@apollo/client'
import orderBy from 'lodash/orderBy'
import { useEffect, useRef, useState } from 'react'

import {
  ArtistDmRoomDetailQuery,
  ArtistDmRoomDetailQueryVariables,
  CreateDmRoomMessageMutation,
  CreateDmRoomMessageMutationVariables,
  DmRoomMessageBoxFragmentDoc,
  DmRoomMessageConnectionFragmentDoc,
  DmRoomMessageCreatedSubscription,
  DmRoomMessageCreatedSubscriptionVariables,
  DmRoomMessageType,
  DmRoomOlderMessagesQuery,
  DmRoomOlderMessagesQueryVariables,
} from '@/clients/utoniq-core/schema'
import { ReplyFormValues } from '@/components/organisms'
import { useCurrentUser, useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { openReportForm } from '@/core/googleForm'

import { Action, consoleDmRoomDetailRef, State } from './template'

const DM_ROOM_MESSAGES_PAGE_LIMIT = 20

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleDmRoomDetail')
  const { user } = useCurrentUser()
  const { router } = useRouter<{ dmRoomId: string }>()
  const { showError } = useMessage()
  const { data, loading: loadingDetail } = useQuery<
    ArtistDmRoomDetailQuery,
    ArtistDmRoomDetailQueryVariables
  >(DM_ROOM_DETAIL, {
    variables: { dmRoomId: router.query.dmRoomId ?? '' },
  })

  /** アクセス前に投稿済みのメッセージを取得 */
  const {
    data: olderMessagesData,
    loading: loadingOlderMessages,
    fetchMore,
  } = useQuery<DmRoomOlderMessagesQuery, DmRoomOlderMessagesQueryVariables>(
    DM_ROOM_OLDER_MESSAGES,
    {
      variables: {
        dmRoomId: router.query.dmRoomId ?? '',
        /**
         * 初回描画時に画面の高さに満たず再フェッチが起こり
         * レイアウトシフトが起きることを避けるため20件取得
         */
        pageInput: { limit: DM_ROOM_MESSAGES_PAGE_LIMIT },
      },
      /**
       * 一覧画面からの遷移時に最新コメント1件を取得したキャッシュにより
       * 1件のみが先に描画され追加フェッチが走ってしまい
       * レイアウトシフトが起きることを避けるため network-only を設定
       */
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError: () => {
        showError(t('error'))
      },
    }
  )

  /** アクセスより前のコメント */
  const olderMessages = orderBy(
    olderMessagesData?.dmRoomMessages?.edges.map(({ node }) => node) ?? [],
    ['createdAt'],
    ['asc']
  )

  /** リアルタイムで投稿された最新メッセージを取得 */
  const { data: dmRoomMessageCreatedData, loading: loadingSubscribedMessages } =
    useSubscription<
      DmRoomMessageCreatedSubscription,
      DmRoomMessageCreatedSubscriptionVariables
    >(DM_ROOM_MESSAGE_CREATED, {
      variables: { dmRoomId: router.query.dmRoomId ?? '' },
      onData: () => {
        setTimeout(() => {
          /**
           * ユーザーがメッセージ最下部付近を表示している場合
           * メッセージリストを最下部へスクロールする
           *
           * スクロールの有無は DmRoomMessageList で判断
           */
          consoleDmRoomDetailRef.current?.toBottom()
        }, 0)
      },
      onError: () => {
        showError(t('error'))
      },
    })

  /** リアルタイムで投稿された最新メッセージを配列化 */
  const [subscribedMessages, setSubscribedMessages] = useState<
    DmRoomMessageCreatedSubscription['dmRoomMessageCreated'][]
  >([])
  useEffect(() => {
    if (!dmRoomMessageCreatedData) return

    setSubscribedMessages(prev => {
      if (
        prev.some(
          message =>
            message?.id === dmRoomMessageCreatedData.dmRoomMessageCreated?.id
        )
      )
        return prev
      return orderBy(
        [...prev, dmRoomMessageCreatedData.dmRoomMessageCreated],
        ['createdAt'],
        ['asc']
      )
    })
  }, [dmRoomMessageCreatedData])

  /** 既にエラーを表示済みかを保持する ref */
  const hasMismatchError = useRef(false)
  /**
   * メッセージが正常に取得できていない場合にエラー表示を行う
   * 1. API で取得した最新1件と WebSocket で取得した最古1件の重複チェック
   * 2. WebSocket で取得したメッセージのシーケンス番号の整合性チェック
   */
  useEffect(() => {
    /**
     * useEffect 内で showError を実行した際
     * 親コンポーネントの state 変更 → 再レンダリング
     * → useEffect の再実行 → showError の実行... のように
     * レンダリングが無限に走ってしまうことを避けるため
     * 一度エラーを表示したら再度エラーを表示しないように制御する
     */
    if (hasMismatchError.current) return

    /** ローディング中はチェックしない */
    if (
      loadingDetail ||
      loadingOlderMessages ||
      loadingSubscribedMessages ||
      olderMessages.length === 0 ||
      subscribedMessages.length === 0
    )
      return

    /** WebSocket から取得した最古のメッセージ */
    const oldestSubscribedMessage = subscribedMessages[0]
    /** WebSocket から取得した最新のメッセージ */
    const newestSubscribedMessage =
      subscribedMessages[subscribedMessages.length - 1]
    /** API で取得した最新のメッセージ */
    const newestOlderMessage = olderMessages[olderMessages.length - 1]

    /**
     * API で取得した最新1件と WebSocket で取得した最古1件の重複チェック
     *
     * 両方のメッセージが存在し ID が一致しない場合は
     * メッセージ取得中に新たなメッセージが追加され差分が出ている可能性が高いため
     * 画面再読み込みでメッセージ取得からやり直してもらうようユーザにエラーを表示する
     *
     * WebSocket 確立中に送信されたメッセージが取得できていなかった場合を想定している
     */
    const hasIdMismatch =
      oldestSubscribedMessage?.id &&
      newestOlderMessage?.id &&
      oldestSubscribedMessage.id !== newestOlderMessage.id

    /**
     * WebSocket で取得したメッセージのシーケンス番号の整合性チェック
     *
     * 2025年10月時点
     *
     * ネイティブアプリにて DM 詳細画面を開いたままバックグラウンドに移行して
     * 再度フォアグラウンドに戻った際に、バックグラウンド中に複数のメッセージを受信していると
     * 最新メッセージ以外が受信できず、メッセージの欠落が発生してしまう
     *
     * 本来はフォアグラウンド復帰時にメッセージの再取得を行うのが好ましいが
     * リリースを優先し、ユーザーに再読み込みを促すエラーを表示する暫定対応を行う
     *
     * フォアグラウンド復帰時にメッセージの再取得を行うロジックを実装後は
     * hasSeqNoMismatch のチェックは削除して問題ない
     */
    const hasSeqNoMismatch =
      (newestSubscribedMessage?.seqNo ?? 0) -
        (oldestSubscribedMessage?.seqNo ?? 0) +
        1 !==
      subscribedMessages.length

    /** いずれかのエラー条件を満たした場合、一度だけエラーを表示 */
    if (hasIdMismatch || hasSeqNoMismatch) {
      hasMismatchError.current = true
      showError(t('mismatchError'))
    }
  }, [
    olderMessages,
    subscribedMessages,
    loadingDetail,
    loadingOlderMessages,
    loadingSubscribedMessages,
  ])

  /**
   * @description
   * 投稿済みのメッセージとリアルタイムで取得したメッセージを結合したもの
   * 投稿済みのメッセージの最新1件はリアルタイムで取得した最古1件と重複するため
   * 除外する処理を入れている
   *
   * nodes に長さを持つ配列が渡されると、loading に関わらず
   * skelton を解除するという ScrollFetcher の実装により、
   * 比較的短時間で取得可能な subscribedMessages だけが
   * 先に表示されてしまうことを防ぐために
   * 各クエリの loading 状態による条件分岐を追加している
   */
  const messages =
    loadingDetail ||
    (loadingOlderMessages && olderMessages.length === 0) ||
    loadingSubscribedMessages
      ? []
      : [
          ...olderMessages
            .filter(message => message !== null && message !== undefined)
            // 重複回避のため最新1件を除外
            .slice(0, -1),
          ...subscribedMessages.filter(
            message => message !== null && message !== undefined
          ),
        ].reverse()

  /** メッセージを作成 */
  const [createDmRoomMessage] = useMutation<
    CreateDmRoomMessageMutation,
    CreateDmRoomMessageMutationVariables
  >(DM_ROOM_MESSAGE_CREATE)

  /** フォームからの入力をもとに、メッセージ作成用の変数を作成 */
  const buildCreateDmRoomMessageVariables = (inputs: ReplyFormValues) => {
    /** テキストメッセージの場合 */
    if (inputs.content) {
      return {
        dmRoomId: router.query.dmRoomId ?? '',
        message: inputs.content,
        type: DmRoomMessageType.Text,
      }
    }
    /** 画像メッセージの場合 */
    return {
      dmRoomId: router.query.dmRoomId ?? '',
      resources: inputs.resources,
      type: DmRoomMessageType.Image,
    }
  }

  return {
    state: {
      loading: loadingDetail || loadingOlderMessages,
      artistId: user?.uid ?? '',
      dmRoom: data?.dmRoom,
      messages: messages,
      hasNextPage: !!olderMessagesData?.dmRoomMessages?.pageInfo.hasNextPage,
    },
    actions: {
      async onReply(inputs) {
        await createDmRoomMessage({
          variables: { inputs: buildCreateDmRoomMessageVariables(inputs) },
        }).catch(error => {
          showError(error)
        })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            dmRoomId: router.query.dmRoomId ?? '',
            pageInput: {
              limit: DM_ROOM_MESSAGES_PAGE_LIMIT,
              cursor: olderMessagesData?.dmRoomMessages?.pageInfo.endCursor,
            },
          },
        }).catch(error => {
          showError(t('error'))
        })
      },
      async onReportUser(contentId, userId, accountId) {
        await openReportForm({
          email: user?.email ?? '',
          contentType: 'dm-rooms',
          contentId,
          userId,
          accountId,
        })
      },
    },
  }
})

const DM_ROOM_DETAIL = gql`
  query ArtistDmRoomDetail($dmRoomId: ID!) {
    dmRoom(dmRoomId: $dmRoomId) {
      id
      title
      artist {
        id
        displayName
        accountId
        avatar {
          uri
          smallUri
        }
      }
      users {
        id
        displayName
        accountId
        avatar {
          uri
          smallUri
        }
      }
    }
  }
`

const DM_ROOM_OLDER_MESSAGES = gql`
  query DmRoomOlderMessages($dmRoomId: ID!, $pageInput: PageInput) {
    dmRoomMessages(dmRoomId: $dmRoomId, pageInput: $pageInput) {
      ...DmRoomMessageConnection
    }
  }
  ${DmRoomMessageConnectionFragmentDoc}
`

const DM_ROOM_MESSAGE_CREATED = gql`
  subscription DmRoomMessageCreated($dmRoomId: ID!) {
    dmRoomMessageCreated(dmRoomId: $dmRoomId) {
      ...DmRoomMessageBox
    }
  }
  ${DmRoomMessageBoxFragmentDoc}
`

const DM_ROOM_MESSAGE_CREATE = gql`
  mutation CreateDmRoomMessage($inputs: CreateDmRoomMessageInput!) {
    createDmRoomMessage(inputs: $inputs) {
      ...DmRoomMessageBox
    }
  }
  ${DmRoomMessageBoxFragmentDoc}
`
